// "How Hush filed this", in plain words, from a purchase's filed_by and its detail. Shared by
// Activity, the edit sheet and the New purchases card.
import type { FiledBy } from './api';

export interface FiledFacts {
  filedBy: FiledBy | null;
  filedDetail: string | null;
  /** The category it is filed under, by name, or null when it has none. */
  category: string | null;
  /** The merchant as shown. */
  merchant: string;
}

/** Filed by Hush itself (an alert or an auto-log), as opposed to by hand or as a transfer. */
export const filedByHush = (f: Pick<FiledFacts, 'filedBy'>) => f.filedBy !== 'manual' && f.filedBy !== 'transfer' && f.filedBy !== 'split';

/**
 * "Filed under Groceries because you filed Costco there 4 times.", "Matched to your Netflix item.
 * Same merchant and amount.", "Hush guessed this from the name. Tap Edit if it's wrong." and so on.
 */
export function filedReason(f: FiledFacts): string {
  switch (f.filedBy) {
    case 'merchant_rule': {
      const n = Number(f.filedDetail);
      const times = n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`;
      return n > 0
        ? `Filed under ${f.category ?? 'its category'} because you filed ${f.merchant} there ${times}.`
        : `Filed under ${f.category ?? 'its category'} because you filed ${f.merchant} there before.`;
    }
    case 'item_match':
      return `Matched to your ${f.filedDetail ?? 'planned'} item. Same merchant and amount.`;
    case 'alias_merge':
      return f.filedDetail === 'logged'
        ? 'Merged with the charge Hush logged for you. Same amount within 2 days.'
        : 'Merged with the entry you added by hand. Same amount within 2 days.';
    case 'recurring_log':
    case 'bill_log':
      return 'Logged for you because no alert had arrived by the due day.';
    case 'suggested':
      return "Hush guessed this from the name. Tap Edit if it's wrong.";
    case 'manual':
      return 'You added or filed this yourself.';
    case 'transfer':
      return 'You marked this money as moved.';
    case 'split':
      return `Your share of a purchase ${f.filedDetail ?? 'your partner'} split with you. You accepted it.`;
    default:
      return f.category ? 'Filed before Hush kept reasons.' : 'Not filed yet. Pick a category.';
  }
}
